import { createStubImplementation } from '@ardoq/stub-implementation';
import {
  ArdoqId,
  FilterInterfaceFilter,
  FormattingFilter,
  QueryBuilderSubquery,
  FilterTypes,
  FilterAttributes,
  ConditionalFormattingAttributes,
  WithTransactionId,
} from '@ardoq/api-types';
import {
  ComponentLabelSource,
  DynamicFilterState,
  FieldValueScopeSource,
  LabelFormattingInfo,
  ReferenceLabelSource,
} from '@ardoq/data-model';
import { returnZero } from '@ardoq/common-helpers';
import { noop } from 'lodash';

export type PerspectivesFilters = {
  filterByWorkspaceIds: ArdoqId[];
  componentFilterRules: QueryBuilderSubquery;
  referenceFilterRules: QueryBuilderSubquery;
  dynamicFilterStates: DynamicFilterState[];
};

export interface FiltersAsQueryBuilderQueries {
  componentRules: QueryBuilderSubquery;
  referenceRules: QueryBuilderSubquery;
}

export type CreateFilterOptions = {
  shouldTriggerChangeEvent?: boolean;
  transactionId?: string;
};

export type GetReferenceLabelFilter = () => FilterInterfaceFilter;
export type GetAllReferenceLabelFilters = () => FilterInterfaceFilter[];
export type GetComponentLabelFilter = () => FilterInterfaceFilter;

export type GetComponentLabelFormattingFields = () => Set<string>;
export type GetLabelFormatting = () => LabelFormattingInfo[];

export type GetNonFormattingFilterCount = () => number;

type CreateFilter = (
  attributes: Omit<FilterInterfaceFilter, 'type'> & {
    filterName?: string;
  },
  options: CreateFilterOptions
) => void;

export type UpdateFiltered = (
  componentId: ArdoqId,
  scopeSource: FieldValueScopeSource | null
) => void;

export type GetFormattingFilters = () => FormattingFilter[];

export type GetConditionalFormattingFilters =
  () => ConditionalFormattingAttributes[];

type ApplyPerspectiveFilters = (filters: PerspectivesFilters) => void;

export type CreateLabelOptions = {
  transactionId?: string;
  includeTime?: boolean;
};

export type CreateReferenceLabelFilter = (
  labelFormattingValue: string,
  options?: CreateLabelOptions
) => void;

export type CreateComponentLabelFilter = (
  labelFormattingValue: string,
  options?: CreateLabelOptions
) => void;

export type CreateLabelFormatting = (
  labelFormatting: LabelFormattingInfo[],
  transactionId?: string
) => void;

export type RemoveReferenceLabelFormattingFilters = (
  shouldTriggerChangeEvent: boolean
) => void;
export type RemoveComponentLabelFormattingFilters = (
  shouldTriggerChangeEvent: boolean
) => void;

type SetConditionalFormattingFilters = (
  formattingRules: FilterAttributes[],
  shouldGreyOut: boolean,
  transactionId?: string
) => void;

export type GetFilterCount = () => number;
export type GetFormattingAndLabelRules = () => WithTransactionId & {
  rules: (FilterInterfaceFilter | FormattingFilter)[];
};

type GetActiveQuickReferenceTypeFilters = () => Set<string> | null;
type GetFiltersAsQueryBuilderQueries = () => FiltersAsQueryBuilderQueries;
type GetColorFiltersForComponent = (modelId: ArdoqId) => string[];
type LoadFilters = (
  filters: FilterAttributes[],
  options?: CreateFilterOptions
) => void;

/**
 * The contract behind `@ardoq/filter-interface`. The exported `filterInterface`
 * singleton is annotated with this type, so the deprecations that consumers see
 * resolve to the property signatures below.
 *
 * Every member is deprecated -- see the doc block on `filterInterface` at the
 * bottom of this file for where to read filter state from instead.
 */
export type FilterInterfaceImplementation = {
  /**
   * @deprecated Join `referenceLabelFilterField$` (`streams/filters/referenceLabelFilterField$.ts`).
   */
  getReferenceLabelFilter: GetReferenceLabelFilter;
  /**
   * @deprecated Join `referenceLabelFilters$` (`streams/filters/referenceLabelFilters$.ts`) and narrow with `filterOperations.isReferenceLabelFormattingFilter` (`@ardoq/core`).
   */
  getAllReferenceLabelFilters: GetAllReferenceLabelFilters;
  /**
   * @deprecated Join `formattingAndLabelRules$` (`streams/filters/formattingAndLabelRules$.ts`) and read the component label rule off the resolved bundle.
   */
  getComponentLabelFilter: GetComponentLabelFilter;
  /**
   * @deprecated Join `formattingAndLabelRules$` and `fields$`, then select with `fieldOperations` (`@ardoq/core`).
   */
  getComponentLabelFormattingFields: GetComponentLabelFormattingFields;
  /**
   * @deprecated Join a derived stream under `streams/filters/` and count with `filterOperations.isStrictlyFormattingFilter` (`@ardoq/core`) rather than reading the Backbone collection.
   */
  getNonFormattingFilterCount: GetNonFormattingFilterCount;
  /**
   * @deprecated Dispatch the owning action in `streams/filters/FilterActions.ts` and let `streams/filters/routines.ts` apply it.
   */
  createFilter: CreateFilter;
  /**
   * @deprecated Dispatch `updateFiltered` from `streams/filters/FilterActions.ts` instead of calling through the interface.
   */
  updateFiltered: UpdateFiltered;
  /**
   * @deprecated Join `formattingAndLabelRules$` (`streams/filters/formattingAndLabelRules$.ts`) and narrow with `filterOperations.isStrictlyFormattingFilter` (`@ardoq/core`).
   */
  getFormattingFilters: GetFormattingFilters;
  /**
   * @deprecated Join `conditionalFormattingFilters$` (`streams/filters/conditionalFormattingFilters$.ts`) and narrow with `filterOperations.isComponentFormatting` / `isReferenceFormatting` (`@ardoq/core`).
   */
  getConditionalFormattingFilters: GetConditionalFormattingFilters;
  /**
   * @deprecated Dispatch the owning action in `streams/filters/FilterActions.ts` and let `streams/filters/routines.ts` apply it.
   */
  applyPerspectiveFilters: ApplyPerspectiveFilters;
  /**
   * @deprecated Dispatch the owning action in `streams/filters/FilterActions.ts` and let `streams/filters/routines.ts` apply it.
   */
  createReferenceLabelFilter: CreateReferenceLabelFilter;
  /**
   * @deprecated Dispatch the owning action in `streams/filters/FilterActions.ts` and let `streams/filters/routines.ts` apply it.
   */
  createComponentLabelFilter: CreateComponentLabelFilter;
  /**
   * @deprecated Dispatch the owning action in `streams/filters/FilterActions.ts` and let `streams/filters/routines.ts` apply it.
   */
  createLabelFormatting: CreateLabelFormatting;
  /**
   * @deprecated Dispatch the owning action in `streams/filters/FilterActions.ts` and let `streams/filters/routines.ts` apply it.
   */
  removeReferenceLabelFormattingFilters: RemoveReferenceLabelFormattingFilters;
  /**
   * @deprecated Dispatch the owning action in `streams/filters/FilterActions.ts` and let `streams/filters/routines.ts` apply it.
   */
  removeComponentLabelFormattingFilters: RemoveComponentLabelFormattingFilters;
  /**
   * @deprecated Dispatch the owning action in `streams/filters/FilterActions.ts` and let `streams/filters/routines.ts` apply it.
   */
  setConditionalFormattingFilters: SetConditionalFormattingFilters;
  /**
   * @deprecated Join a derived stream under `streams/filters/` and count the resolved filters, rather than reading the Backbone collection at call time.
   */
  getFilterCount: GetFilterCount;
  /**
   * @deprecated Join `quickPerspectiveReferenceTypeFilters$` (`streams/filters/quickPerspectiveReferenceTypeFilters$.ts`).
   */
  getActiveQuickReferenceTypeFilters: GetActiveQuickReferenceTypeFilters;
  /**
   * @deprecated Join `activeFilter$` (`streams/filters/activeFilter$.ts`) and read `activeFilters`.
   */
  getFiltersAsQueryBuilderQueries: GetFiltersAsQueryBuilderQueries;
  /**
   * @deprecated Join `activeComponentColors$` (`streams/filters/activeComponentColors$.ts`), or call `filterColorDataOperations.getComponentFilterColor` (`@ardoq/scope-data`).
   */
  getColorFiltersForComponent: GetColorFiltersForComponent;
  /**
   * @deprecated Join `formattingAndLabelRules$` (`streams/filters/formattingAndLabelRules$.ts`) and read the label formatting off the resolved bundle.
   */
  getLabelFormatting: GetLabelFormatting;
  /**
   * @deprecated Dispatch the owning action in `streams/filters/FilterActions.ts` and let `streams/filters/routines.ts` apply it.
   */
  loadFilters: LoadFilters;
  /**
   * @deprecated Join `formattingAndLabelRules$` (`streams/filters/formattingAndLabelRules$.ts`) -- it exposes exactly this bundle.
   */
  getFormattingAndLabelRules: GetFormattingAndLabelRules;
};

export const DEFAULT_REFERENCE_LABEL_FILTER: FilterInterfaceFilter = {
  affectComponent: false,
  affectReference: true,
  isNegative: false,
  name: '',
  type: FilterTypes.REFERENCE_LABEL,
  value: ReferenceLabelSource.DISPLAY_TEXT_OR_REFERENCE_TYPE,
};

const defaultImplementation: FilterInterfaceImplementation = {
  getReferenceLabelFilter: () => DEFAULT_REFERENCE_LABEL_FILTER,
  getAllReferenceLabelFilters: () => [],
  getComponentLabelFilter: () => ({
    affectComponent: true,
    affectReference: false,
    isNegative: false,
    name: '',
    type: FilterTypes.COMPONENT_LABEL,
    value: ComponentLabelSource.NAME,
  }),
  getComponentLabelFormattingFields: () => new Set(),
  getNonFormattingFilterCount: returnZero,
  createFilter: noop,
  updateFiltered: noop,
  applyPerspectiveFilters: noop,
  getFormattingFilters: () => [],
  getConditionalFormattingFilters: () => [],
  createReferenceLabelFilter: returnZero,
  createComponentLabelFilter: returnZero,
  createLabelFormatting: noop,
  removeReferenceLabelFormattingFilters: returnZero,
  removeComponentLabelFormattingFilters: returnZero,
  setConditionalFormattingFilters: returnZero,
  getFilterCount: returnZero,
  getActiveQuickReferenceTypeFilters: () => null,
  getFiltersAsQueryBuilderQueries: () => ({}) as FiltersAsQueryBuilderQueries,
  getColorFiltersForComponent: () => [],
  getLabelFormatting: () => [],
  loadFilters: noop,
  getFormattingAndLabelRules: () => ({ rules: [] }),
};

const {
  getReferenceLabelFilter,
  getAllReferenceLabelFilters,
  getComponentLabelFilter,
  getComponentLabelFormattingFields,
  getNonFormattingFilterCount,
  createFilter,
  updateFiltered,
  getFormattingFilters,
  getConditionalFormattingFilters,
  applyPerspectiveFilters,
  createReferenceLabelFilter,
  createComponentLabelFilter,
  createLabelFormatting,
  getLabelFormatting,
  removeReferenceLabelFormattingFilters,
  removeComponentLabelFormattingFilters,
  setConditionalFormattingFilters,
  registerImplementation,
  getFilterCount,
  getActiveQuickReferenceTypeFilters,
  getFiltersAsQueryBuilderQueries,
  getColorFiltersForComponent,
  loadFilters,
  getFormattingAndLabelRules,
} = createStubImplementation('@ardoq/filter-interface', defaultImplementation);

/**
 * `filterInterface` is a Backbone-era singleton, and the one whose source of
 * truth has not moved yet: the filters still live in the Backbone collection
 * `globalFilters` (`collections/filters.ts`), so there is no canonical `filters$`
 * state stream to point at.
 *
 * What exists instead is a set of derived streams under `streams/filters/` --
 * `formattingAndLabelRules$`, `conditionalFormattingFilters$`,
 * `referenceLabelFilters$`, `referenceLabelFilterField$`,
 * `activeComponentColors$`, `quickPerspectiveReferenceTypeFilters$`,
 * `includedInContext$`, `activeFilter$`, and `componentFilterAttributes$` /
 * `referenceFilterAttributes$`. Those streams are the boundary that owns the
 * imperative read; join them and thread the resolved rules downstream as plain
 * parameters instead of calling this interface from leaf code. Pure logic belongs
 * in `filterOperations` (`@ardoq/core`), `filterColorDataOperations`
 * (`@ardoq/scope-data`) or the pure helper modules in `@ardoq/filter-helpers`.
 *
 * Mutating members dispatch through `streams/filters/FilterActions.ts`; prefer
 * dispatching the action over calling the mutator.
 *
 * Every member below is deprecated. See
 * `packages/eslint-plugin-ardoq-lint/docs/rules/no-model-interface-call.md`
 * for the hoisting recipe.
 */
export const filterInterface: FilterInterfaceImplementation & {
  defaultImplementation: FilterInterfaceImplementation;
  registerImplementation: (
    implementation: FilterInterfaceImplementation
  ) => void;
} = {
  /**
   * @deprecated Join `referenceLabelFilterField$` (`streams/filters/referenceLabelFilterField$.ts`).
   */
  getReferenceLabelFilter,
  /**
   * @deprecated Join `referenceLabelFilters$` (`streams/filters/referenceLabelFilters$.ts`) and narrow with `filterOperations.isReferenceLabelFormattingFilter` (`@ardoq/core`).
   */
  getAllReferenceLabelFilters,
  /**
   * @deprecated Join `formattingAndLabelRules$` (`streams/filters/formattingAndLabelRules$.ts`) and read the component label rule off the resolved bundle.
   */
  getComponentLabelFilter,
  /**
   * @deprecated Join `formattingAndLabelRules$` and `fields$`, then select with `fieldOperations` (`@ardoq/core`).
   */
  getComponentLabelFormattingFields,
  /**
   * @deprecated Join a derived stream under `streams/filters/` and count with `filterOperations.isStrictlyFormattingFilter` (`@ardoq/core`) rather than reading the Backbone collection.
   */
  getNonFormattingFilterCount,
  /**
   * @deprecated Dispatch the owning action in `streams/filters/FilterActions.ts` and let `streams/filters/routines.ts` apply it.
   */
  createFilter,
  /**
   * @deprecated Dispatch `updateFiltered` from `streams/filters/FilterActions.ts` instead of calling through the interface.
   */
  updateFiltered,
  /**
   * @deprecated Join `formattingAndLabelRules$` (`streams/filters/formattingAndLabelRules$.ts`) and narrow with `filterOperations.isStrictlyFormattingFilter` (`@ardoq/core`).
   */
  getFormattingFilters,
  /**
   * @deprecated Join `conditionalFormattingFilters$` (`streams/filters/conditionalFormattingFilters$.ts`) and narrow with `filterOperations.isComponentFormatting` / `isReferenceFormatting` (`@ardoq/core`).
   */
  getConditionalFormattingFilters,
  /**
   * @deprecated Dispatch the owning action in `streams/filters/FilterActions.ts` and let `streams/filters/routines.ts` apply it.
   */
  applyPerspectiveFilters,
  /**
   * @deprecated Dispatch the owning action in `streams/filters/FilterActions.ts` and let `streams/filters/routines.ts` apply it.
   */
  createReferenceLabelFilter,
  /**
   * @deprecated Dispatch the owning action in `streams/filters/FilterActions.ts` and let `streams/filters/routines.ts` apply it.
   */
  createComponentLabelFilter,
  /**
   * @deprecated Dispatch the owning action in `streams/filters/FilterActions.ts` and let `streams/filters/routines.ts` apply it.
   */
  createLabelFormatting,
  /**
   * @deprecated Join `formattingAndLabelRules$` (`streams/filters/formattingAndLabelRules$.ts`) and read the label formatting off the resolved bundle.
   */
  getLabelFormatting,
  /**
   * @deprecated Dispatch the owning action in `streams/filters/FilterActions.ts` and let `streams/filters/routines.ts` apply it.
   */
  removeReferenceLabelFormattingFilters,
  /**
   * @deprecated Dispatch the owning action in `streams/filters/FilterActions.ts` and let `streams/filters/routines.ts` apply it.
   */
  removeComponentLabelFormattingFilters,
  /**
   * @deprecated Dispatch the owning action in `streams/filters/FilterActions.ts` and let `streams/filters/routines.ts` apply it.
   */
  setConditionalFormattingFilters,
  registerImplementation,
  /**
   * @deprecated Join a derived stream under `streams/filters/` and count the resolved filters, rather than reading the Backbone collection at call time.
   */
  getFilterCount,
  /**
   * @deprecated Join `quickPerspectiveReferenceTypeFilters$` (`streams/filters/quickPerspectiveReferenceTypeFilters$.ts`).
   */
  getActiveQuickReferenceTypeFilters,
  /**
   * @deprecated Join `activeFilter$` (`streams/filters/activeFilter$.ts`) and read `activeFilters`.
   */
  getFiltersAsQueryBuilderQueries,
  /**
   * @deprecated Join `activeComponentColors$` (`streams/filters/activeComponentColors$.ts`), or call `filterColorDataOperations.getComponentFilterColor` (`@ardoq/scope-data`).
   */
  getColorFiltersForComponent,
  defaultImplementation,
  /**
   * @deprecated Dispatch the owning action in `streams/filters/FilterActions.ts` and let `streams/filters/routines.ts` apply it.
   */
  loadFilters,
  /**
   * @deprecated Join `formattingAndLabelRules$` (`streams/filters/formattingAndLabelRules$.ts`) -- it exposes exactly this bundle.
   */
  getFormattingAndLabelRules,
};
